import React, { useEffect, useRef, useState } from 'react'
import AllProductsStyle from '../AllProducts/AllProducts'
import Button from '../../components/Button'
import Layout from '../../components/Layout'
import { ErrorModal } from '../../components/Modal'
import ClipLoader from 'react-spinners/ClipLoader'
import { cashbackApi } from '../../api'
import { get } from 'lodash'
import { isAxiosError } from 'axios'
import { useSelector } from 'react-redux'
import moment from 'moment'
import dayjs from 'dayjs'
import { DatePicker } from 'antd'
import { BG_COLOR } from '../../constants/colors'

const { RangePicker } = DatePicker
const dateFormat = 'YYYY-MM-DD'

const CashbackSummary = () => {
	const { getMe } = useSelector((state) => state.main)
	const hasAccess = ['Admin', 'Kassir'].includes(get(getMe, 'U_JobTitle', ''))

	const errorRef = useRef()

	const [isLoading, setIsLoading] = useState(false)
	const [summary, setSummary] = useState(null)
	const [dateStart, setDateStart] = useState(
		moment().startOf('month').format(dateFormat),
	)
	const [dateEnd, setDateEnd] = useState(moment().format(dateFormat))

	const onRangeChange = (dates) => {
		if (dates) {
			setDateStart(dates[0].format(dateFormat))
			setDateEnd(dates[1].format(dateFormat))
		} else {
			setDateStart('')
			setDateEnd('')
		}
	}

	const getSummary = () => {
		setIsLoading(true)
		cashbackApi
			.get('reports/summary', {
				params: {
					from: dateStart
						? moment(dateStart).startOf('day').toISOString()
						: undefined,
					to: dateEnd
						? moment(dateEnd).endOf('day').toISOString()
						: undefined,
				},
			})
			.then((res) => {
				setSummary(res.data)
			})
			.catch((err) => {
				if (isAxiosError(err))
					errorRef.current?.open(
						get(err, 'response.data.message', get(err, 'message', '')),
					)
			})
			.finally(() => setIsLoading(false))
	}

	useEffect(() => {
		if (hasAccess) getSummary()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const currency = get(summary, 'currency', '')

	const cards = [
		{ title: 'Начислено', value: get(summary, 'totalEarned', 0), suffix: currency },
		{ title: 'Использовано', value: get(summary, 'totalUsed', 0), suffix: currency },
		{ title: 'Возвращено', value: get(summary, 'totalReversed', 0), suffix: currency },
		{ title: 'Активный баланс', value: get(summary, 'activeBalance', 0), suffix: currency },
		{ title: 'Кол-во оценок', value: get(summary, 'ratingCount', 0) },
		{ title: 'Средняя оценка', value: get(summary, 'averageRating', 0) },
		{ title: 'Кол-во рефералов', value: get(summary, 'referralCount', 0) },
		{ title: 'Сумма рефералов', value: get(summary, 'referralAmount', 0), suffix: currency },
		{ title: 'Кол-во жалоб', value: get(summary, 'complaintCount', 0) },
	]

	if (!hasAccess) {
		return (
			<Layout>
				<AllProductsStyle style={{ backgroundColor: BG_COLOR }}>
					<div className="container">
						<p className="productTitle">Нет доступа</p>
					</div>
				</AllProductsStyle>
			</Layout>
		)
	}

	return (
		<Layout>
			<AllProductsStyle
				style={{
					backgroundColor: BG_COLOR,
				}}
			>
				<div className="container">
					<p className="productTitle">Отчёт по кэшбэку</p>
					<div className="flex items-end justify-between my-8 flex-wrap gap-4">
						<div className="flex items-end gap-4 flex-wrap">
							<div className="flex flex-col gap-1">
								<p className="text-sm font-medium">Период</p>
								<RangePicker
									value={[
										dateStart ? dayjs(dateStart, dateFormat) : null,
										dateEnd ? dayjs(dateEnd, dateFormat) : null,
									]}
									format={dateFormat}
									onChange={onRangeChange}
								/>
							</div>
							<Button
								className="!h-10"
								onClick={getSummary}
								isLoading={isLoading}
							>
								Поиск
							</Button>
						</div>
						{summary && (
							<p className="text-sm text-gray-500">
								{moment(get(summary, 'from')).format('DD-MM-YYYY')} —{' '}
								{moment(get(summary, 'to')).format('DD-MM-YYYY')}
							</p>
						)}
					</div>

					{isLoading ? (
						<ClipLoader
							loading={true}
							size={75}
							aria-label="Loading Spinner"
							className={'loader'}
							data-testid="loader"
						/>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
							{cards.map((card, i) => (
								<div
									key={i}
									className="bg-gray-100 drop-shadow-md p-4 rounded-xl min-h-24"
								>
									<p className="text-sm text-gray-600">{card.title}</p>
									<p className="text-2xl font-bold text-blue-900 mt-2">
										{card.value} {card.suffix || ''}
									</p>
								</div>
							))}
						</div>
					)}
				</div>
			</AllProductsStyle>
			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
		</Layout>
	)
}

export default CashbackSummary
